import { useState } from "react";
import { Play, X, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { DECK_THEME as T } from "../../lib/deckModel";
import { imgSrc } from "./DeckUI";

export const embedUrl = (u) => {
  const s = String(u || "").trim();
  const yt = s.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?autoplay=1&rel=0`;
  const vm = s.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}?autoplay=1`;
  return null;
};

export function WatchButton({ video }) {
  const [open, setOpen] = useState(false);
  if (!video?.url) return null;
  const embed = video.kind === "link" ? embedUrl(video.url) : null;
  return (
    <>
      <button onClick={() => setOpen(true)} className="group mt-10 inline-flex items-center gap-3 rounded-full border border-white/30 bg-white/10 pl-1.5 pr-6 py-1.5 text-white backdrop-blur-md hover:bg-white/15 transition-colors" data-testid="deck-watch-btn">
        <span className="h-11 w-11 rounded-full grid place-items-center transition-transform duration-300 group-hover:scale-105" style={{ background: T.accent }}><Play className="h-4 w-4 ml-0.5" fill="currentColor" /></span>
        <span className="text-[15px] tracking-wide">{video.label || "Watch the Project"}</span>
      </button>
      {open && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 sm:p-10" style={{ background: "rgba(10,20,32,0.92)" }} onClick={() => setOpen(false)} data-testid="deck-video-modal">
          <button onClick={() => setOpen(false)} className="absolute top-5 right-5 h-10 w-10 rounded-full grid place-items-center text-white bg-white/10 hover:bg-white/20 transition-colors" data-testid="deck-video-close"><X className="h-5 w-5" /></button>
          <div className="w-full max-w-6xl aspect-video rounded-2xl overflow-hidden bg-black shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {embed ? <iframe src={embed} title="Project video" className="h-full w-full" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen data-testid="deck-video-embed" />
              : <video src={imgSrc(video.url)} controls autoPlay playsInline className="h-full w-full" data-testid="deck-video-player" />}
          </div>
        </div>
      )}
    </>
  );
}

const CHUNK_RETRIES = 3;
async function uploadVideo(file, onProgress) {
  const { data: init } = await api.post("/decks/videos/init", { size: file.size, content_type: file.type || "video/mp4", filename: file.name });
  for (let n = 0; n < init.parts; n++) {
    const blob = file.slice(n * init.chunk_size, Math.min(file.size, (n + 1) * init.chunk_size));
    for (let t = 1; ; t++) {
      try { await api.put(`/decks/videos/${init.id}/chunk/${n}`, blob, { headers: { "Content-Type": "application/octet-stream" } }); break; }
      catch (e) { if (t >= CHUNK_RETRIES) throw e; }
    }
    onProgress(Math.round(((n + 1) / init.parts) * 100));
  }
  const { data } = await api.post(`/decks/videos/${init.id}/complete`);
  return data.url;
}

const inp = "w-full rounded-md border border-border bg-secondary/30 px-2.5 py-1.5 text-sm outline-none focus:border-primary";

export function VideoEditor({ video = {}, onChange }) {
  const [pct, setPct] = useState(null);
  const set = (p) => onChange({ ...video, ...p });
  const onFile = async (f) => {
    if (!f) return;
    if (!f.type.startsWith("video/")) return toast.error("Choose a video file (MP4 recommended)");
    setPct(0);
    try { set({ kind: "upload", url: await uploadVideo(f, setPct), filename: f.name }); toast.success("Video uploaded"); }
    catch (e) { toast.error(apiError(e)); } finally { setPct(null); }
  };
  return (
    <div className="space-y-2 rounded-lg border border-border p-3" data-testid="deck-video-editor">
      <div className="text-xs font-semibold">Hero video</div>
      <div className="space-y-1"><label className="text-[10px] uppercase tracking-wide text-muted-foreground">Button text</label>
        <input value={video.label || ""} placeholder="Watch the Project" onChange={(e) => set({ label: e.target.value })} className={inp} data-testid="deck-video-label" /></div>
      <div className="space-y-1"><label className="text-[10px] uppercase tracking-wide text-muted-foreground">YouTube / Vimeo link</label>
        <input value={video.kind === "link" ? video.url || "" : ""} placeholder="https://youtu.be/…" onChange={(e) => set({ kind: "link", url: e.target.value })} className={inp} data-testid="deck-video-link" /></div>
      <label className={`flex items-center gap-2 rounded-md border border-dashed border-border px-3 py-2 text-sm ${pct === null ? "cursor-pointer hover:bg-secondary/40" : "opacity-70"}`} data-testid="deck-video-upload">
        {pct === null ? <Upload className="h-4 w-4" /> : <Loader2 className="h-4 w-4 animate-spin" />}
        {pct === null ? (video.kind === "upload" && video.url ? `Replace uploaded video (${video.filename || "MP4"})` : "Upload MP4 (up to ~500 MB)") : `Uploading… ${pct}%`}
        <input type="file" accept="video/*" className="hidden" disabled={pct !== null} onChange={(e) => onFile(e.target.files?.[0])} />
      </label>
      {pct !== null && <div className="h-1.5 rounded-full bg-secondary"><div className="h-1.5 rounded-full bg-primary transition-[width]" style={{ width: `${pct}%` }} data-testid="deck-video-progress" /></div>}
      {video.url && <button onClick={() => onChange({ label: video.label })} className="text-[11px] text-muted-foreground hover:text-destructive" data-testid="deck-video-remove">Remove video</button>}
    </div>
  );
}
